import React from "react";
import { Upload } from "@/components/forms/Upload";
import { useMutation } from "@apollo/client";
import { UPDATE_USER_MUTATION } from "@/graphql/mutations";

type Props = {
  initial?: any;
  onSubmit: (values: any) => Promise<void>;
  autosave: (patch: Record<string, any>, merge?: boolean) => void;
  saving: boolean;
};

const BasicsStep: React.FC<Props> = ({ initial, onSubmit, autosave, saving }) => {
  const [updateUser] = useMutation(UPDATE_USER_MUTATION);
  const [uploading, setUploading] = React.useState(false);
  const [uploadError, setUploadError] = React.useState<string | null>(null);
  const [form, setForm] = React.useState({
    stylingPreference: initial?.stylingPreference || "womans",
    termsAccepted: Boolean(initial?.termsAcceptedAt),
    privacyAccepted: Boolean(initial?.privacyAcceptedAt),
  });

  const update = (k: string, v: any) => {
    const next = { ...form, [k]: v } as any;
    setForm(next);
    autosave({ step: { basics: next } });
  };

  const handleUpload = async (signedIds: string[]) => {
    if (!signedIds || signedIds.length === 0) {
      setUploadError("No file was uploaded. Please try again.");
      return;
    }
    const signedId = signedIds[0];
    setUploading(true);
    setUploadError(null);
    try {
      await updateUser({
        variables: { fieldName: "avatar", fieldValue: signedId },
      });
    } catch (e: any) {
      setUploadError(e?.message || "Failed to save profile photo. Please try again.");
    } finally {
      setUploading(false);
    }
  };


  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit({
      stylingPreference: form.stylingPreference,
      termsAccepted: form.termsAccepted || undefined,
      privacyAccepted: form.privacyAccepted || undefined,
    });
  };

  return (
    <form onSubmit={submit} className="space-y-12">
      <div className="text-center">
        <h2 className="text-xl font-light tracking-wider uppercase">Upload a Profile Photo</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
          Add a photo so we can get a better sense of your style. You can change this later.
        </p>
        <div className="mt-6 max-w-md mx-auto">
          <Upload multiple={false} onUpload={handleUpload} label="Upload a photo" />
          {uploading && <div className="text-sm text-gray-500 mt-2">Uploading...</div>}
          {uploadError && <div className="text-sm text-red-500 mt-2">{uploadError}</div>}
        </div>
      </div>

      <div className="text-center">
        <h2 className="text-xl font-light tracking-wider uppercase">Styling Preference</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
          Which collection would you like to build your style from?
        </p>
        <div className="mt-6 flex justify-center gap-4">
          <label className="relative flex items-center">
            <input
              type="radio"
              name="styling-preference"
              value="womans"
              checked={form.stylingPreference === "womans"}
              onChange={(e) => update("stylingPreference", e.target.value)}
              className="peer absolute opacity-0 h-0 w-0"
            />
            <div className="cursor-pointer rounded-md border border-gray-300 dark:border-gray-700 px-6 py-3 text-sm font-medium transition-all peer-checked:bg-black peer-checked:text-white peer-checked:border-black dark:peer-checked:bg-white dark:peer-checked:text-black dark:peer-checked:border-white">
              Woman's Clothing
            </div>
          </label>
          <label className="relative flex items-center">
            <input
              type="radio"
              name="styling-preference"
              value="mens"
              checked={form.stylingPreference === "mens"}
              onChange={(e) => update("stylingPreference", e.target.value)}
              className="peer absolute opacity-0 h-0 w-0"
            />
            <div className="cursor-pointer rounded-md border border-gray-300 dark:border-gray-700 px-6 py-3 text-sm font-medium transition-all peer-checked:bg-black peer-checked:text-white peer-checked:border-black dark:peer-checked:bg-white dark:peer-checked:text-black dark:peer-checked:border-white">
              Man's Clothing
            </div>
          </label>
        </div>
      </div>

      <div className="flex items-center justify-between pt-6 border-t border-gray-200 dark:border-gray-800">
        <div className="space-y-2">
          <label className="relative flex items-center gap-3 cursor-pointer text-sm">
            <input
              type="checkbox"
              checked={form.termsAccepted}
              onChange={(e) => update("termsAccepted", e.target.checked)}
              className="peer absolute opacity-0 h-0 w-0"
            />
            <span className="w-5 h-5 border-2 border-gray-300 dark:border-gray-600 rounded transition-all flex items-center justify-center peer-checked:bg-black dark:peer-checked:bg-white peer-checked:border-black dark:peer-checked:border-white">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-white dark:text-black opacity-0 peer-checked:opacity-100" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </span>
            I agree to the <a href="/terms" target="_blank" className="underline">Terms of Service</a>
          </label>
          <label className="relative flex items-center gap-3 cursor-pointer text-sm">
            <input
              type="checkbox"
              checked={form.privacyAccepted}
              onChange={(e) => update("privacyAccepted", e.target.checked)}
              className="peer absolute opacity-0 h-0 w-0"
            />
            <span className="w-5 h-5 border-2 border-gray-300 dark:border-gray-600 rounded transition-all flex items-center justify-center peer-checked:bg-black dark:peer-checked:bg-white peer-checked:border-black dark:peer-checked:border-white">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-white dark:text-black opacity-0 peer-checked:opacity-100" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </span>
            I agree to the <a href="/privacy" target="_blank" className="underline">Privacy Policy</a>
          </label>
        </div>

        <button
          type="submit"
          className="w-full md:w-auto uppercase tracking-wider font-semibold text-sm rounded-md border border-transparent bg-black dark:bg-white text-white dark:text-black px-8 py-3 transition-all hover:bg-gray-800 dark:hover:bg-gray-200 disabled:opacity-50"
          disabled={saving || uploading || !form.termsAccepted || !form.privacyAccepted}
        >
          {saving ? "Saving..." : "Next"}
        </button>
      </div>
    </form>
  );
};

export default BasicsStep;
